import type { Appearance } from "../types";
import { CATALOG, appearanceCode, withDefaults, type LayerDef, type PartKind, type Ramp } from "./catalog";

/**
 * Compoe o spritesheet de um avatar a partir das partes com cores-marcador
 * (ver tools/genassets/avatar.mjs). Um marcador e o pixel
 * rgb(255, canal*4 + tom, 255): canal 0 pele, 1 cabelo, 2 roupa de cima,
 * 3 de baixo, 4 sapato, 5 segunda cor de cima, 6 acessorio de cabeca;
 * tom 0 luz, 1 base, 2 sombra, 3 contorno. Azul entre 128 e 254 = o mesmo
 * marcador translucido (degrade do cabelo).
 *
 * O mesmo canvas serve as texturas do Phaser, a miniatura do HUD e a previa
 * do customizador.
 */

const PART_URL = (file: string) => `/assets/avatar/${file}.png`;

const images = new Map<string, HTMLImageElement>();
const sheets = new Map<string, HTMLCanvasElement>();
let loading: Promise<void> | null = null;

/** Arquivo da peca `id` na camada que desenha o campo `field`. */
function partFile(field: PartKind, id: string): string {
  const layer = CATALOG.layers.find((l) => l.field === field);
  if (!layer) throw new Error(`camada sem campo ${field}`);
  return layer.file.replace("{id}", id);
}

/** Todos os arquivos de partes (as opcoes "nenhum" nao tem arquivo) e os recortes. */
function allPartFiles(): string[] {
  const files: string[] = [];
  for (const layer of CATALOG.layers) {
    if (!layer.field) {
      files.push(layer.file);
      continue;
    }
    for (const { id, cut } of CATALOG.parts[layer.field]) {
      if (id === CATALOG.none) continue;
      files.push(layer.file.replace("{id}", id));
      if (cut) files.push(`${layer.file.replace("{id}", id)}_cut`);
    }
  }
  return files;
}

/** Carrega todas as partes (~30 PNGs pequenos). Chamar uma vez antes de compor. */
export function loadAvatarParts(): Promise<void> {
  loading ??= Promise.all(
    allPartFiles().map(
      (file) =>
        new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            images.set(file, img);
            resolve();
          };
          img.onerror = () => reject(new Error(`falha ao carregar a parte do avatar ${file}`));
          img.src = PART_URL(file);
        }),
    ),
  ).then(() => undefined);
  return loading;
}

export function avatarPartsReady(): boolean {
  return images.size > 0 && images.size === allPartFiles().length;
}

/** Arquivo de uma camada para a aparencia (null = peca "nenhum", camada vazia). */
function layerFile(layer: LayerDef, a: Appearance): string | null {
  if (!layer.field) return layer.file;
  const id = a[layer.field];
  return id === CATALOG.none ? null : layer.file.replace("{id}", id);
}

/** Recorte que a peca do campo `cutBy` faz nesta camada, se houver. */
function cutFile(layer: LayerDef, a: Appearance): string | null {
  if (!layer.cutBy) return null;
  const id = a[layer.cutBy];
  const part = CATALOG.parts[layer.cutBy].find((p) => p.id === id);
  return part?.cut ? `${partFile(layer.cutBy, id)}_cut` : null;
}

function loaded(file: string): HTMLImageElement {
  const img = images.get(file);
  if (!img) throw new Error(`parte do avatar não carregada: ${file}`);
  return img;
}

/** Rampas na ordem dos canais dos marcadores. */
function paletteOf(a: Appearance): Ramp[] {
  const { skin, hair, cloth } = CATALOG.colors;
  return [
    skin[a.skinTone].ramp,
    hair[a.hairColor].ramp,
    cloth[a.topColor].ramp,
    cloth[a.bottomColor].ramp,
    cloth[a.shoesColor].ramp,
    cloth[a.topColor2].ramp,
    cloth[a.headwearColor].ramp,
  ];
}

/** Troca os marcadores pelas cores da paleta, no lugar. */
function recolor(ctx: CanvasRenderingContext2D, w: number, h: number, palette: Ramp[]): void {
  const pixels = ctx.getImageData(0, 0, w, h);
  const d = pixels.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0 || d[i] !== 255 || d[i + 2] < 128 || d[i + 1] >= CATALOG.channels * 4) continue;
    const c = palette[d[i + 1] >> 2][d[i + 1] & 3];
    // azul abaixo de 255 = marcador translucido: o azul guarda a opacidade
    // (128..254 = 0..1), porque o navegador estraga marcadores semitransparentes
    if (d[i + 2] < 255) d[i + 3] = Math.round(((d[i + 2] - 128) / 126) * d[i + 3]);
    d[i] = c[0];
    d[i + 1] = c[1];
    d[i + 2] = c[2];
  }
  ctx.putImageData(pixels, 0, 0);
}

/** Spritesheet completo (grid do catalogo, frames de 32x48). Fica em cache por aparencia. */
export function composeSheet(appearance: Appearance): HTMLCanvasElement {
  const a = withDefaults(appearance);
  const code = appearanceCode(a);
  const cached = sheets.get(code);
  if (cached) return cached;

  const { width, height, cols, rows } = CATALOG.frame;
  const canvas = document.createElement("canvas");
  canvas.width = width * cols;
  canvas.height = height * rows;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("canvas 2d indisponível");
  ctx.imageSmoothingEnabled = false;

  // Cada camada e recolorida sozinha antes de empilhar: pixels translucidos
  // (a sombra da barba por fazer) se misturam com a cor final de baixo, e o
  // chapeu pode apagar o cabelo acima da aba sem apagar o resto.
  const layerCanvas = document.createElement("canvas");
  layerCanvas.width = canvas.width;
  layerCanvas.height = canvas.height;
  const lctx = layerCanvas.getContext("2d", { willReadFrequently: true });
  if (!lctx) throw new Error("canvas 2d indisponível");
  lctx.imageSmoothingEnabled = false;

  const palette = paletteOf(a);
  for (const layer of CATALOG.layers) {
    const file = layerFile(layer, a);
    if (!file) continue;
    lctx.globalCompositeOperation = "source-over";
    lctx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
    lctx.drawImage(loaded(file), 0, 0);
    recolor(lctx, layerCanvas.width, layerCanvas.height, palette);
    const cut = cutFile(layer, a);
    if (cut) {
      lctx.globalCompositeOperation = "destination-out";
      lctx.drawImage(loaded(cut), 0, 0);
    }
    ctx.drawImage(layerCanvas, 0, 0);
  }

  sheets.set(code, canvas);
  return canvas;
}

/**
 * Desenha um frame do avatar num canvas de destino, opcionalmente recortado
 * (usado nas miniaturas de cabelo/roupa do customizador).
 */
export function drawFrame(
  target: HTMLCanvasElement,
  a: Appearance,
  frame: number,
  crop: { y: number; h: number } = { y: 0, h: CATALOG.frame.height },
): void {
  const { width, height, cols } = CATALOG.frame;
  const sheet = composeSheet(a);
  const ctx = target.getContext("2d");
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, target.width, target.height);
  const sx = (frame % cols) * width;
  const sy = Math.floor(frame / cols) * height + crop.y;
  ctx.drawImage(sheet, sx, sy, width, crop.h, 0, 0, target.width, target.height);
}

/** data: URL do spritesheet, para usar como background-image (HUD). */
export function sheetDataURL(a: Appearance): string {
  return composeSheet(a).toDataURL("image/png");
}

/**
 * Recorte quadrado da cabeca no frame de frente (com chapeu e cabelo alto),
 * em pixels do frame de 32x48.
 */
const HEAD_CROP = { x: 2, y: 0, size: 28 } as const;
const heads = new Map<string, string>();

/** data: URL da cabeca do avatar (barra de mensagens privadas). Em cache por aparencia. */
export function headDataURL(a: Appearance): string {
  const look = withDefaults(a);
  const code = appearanceCode(look);
  const cached = heads.get(code);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = HEAD_CROP.size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.imageSmoothingEnabled = false;
  const { x, y, size } = HEAD_CROP;
  ctx.drawImage(composeSheet(look), x, y, size, size, 0, 0, size, size);
  const url = canvas.toDataURL("image/png");
  heads.set(code, url);
  return url;
}
